// src/components/PaymentModal.jsx
import { useState, useEffect } from "react";
import { api } from "../utils/api";

export default function PaymentModal({ 
  isOpen, 
  onClose, 
  amount, 
  orderId, 
  paymentMethod,
  onSuccess 
}) {
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && orderId) {
      initiatePayment();
    }
  }, [isOpen, orderId]);

  const initiatePayment = async () => {
    setLoading(true);
    setError(null);

    const data = { amount, orderId };

    const res = paymentMethod === "UPI"
      ? await api.initiateUPIPayment(data)
      : await api.initiatePayPalPayment(data);

    setLoading(false);

    if (res.success) {
      setPaymentData(res.data);
    } else {
      setError(res.message);
    }
  };

  const handleVerifyPayment = async () => {
    if (!paymentData?.paymentId) return;

    setVerifying(true);
    setError(null);

    const res = paymentMethod === "UPI"
      ? await api.verifyUPIPayment({ paymentId: paymentData.paymentId })
      : await api.verifyPayPalPayment({ paymentId: paymentData.paymentId });

    setVerifying(false);

    if (res.success && res.data.status === "success") {
      onSuccess(res.data);
    } else {
      setError(res.message || "Payment verification failed. Please try again.");
    }
  };

  const handlePayPalRedirect = () => {
    if (paymentData?.approvalUrl) {
      window.open(paymentData.approvalUrl, "_blank");
      // Auto-verify after 5 seconds (in real scenario, user would be redirected back)
      setTimeout(() => {
        handleVerifyPayment();
      }, 5000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">
            {paymentMethod === "UPI" ? "UPI Payment" : "PayPal Payment"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p>Initiating payment...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {!loading && paymentData && paymentMethod === "UPI" && (
          <div className="text-center">
            <p className="text-lg font-semibold mb-2">
              Amount: ₹{amount.toLocaleString()}
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Payment ID: {paymentData.paymentId}
            </p>

            {/* QR Code */}
            <div className="bg-gray-100 p-4 rounded-lg mb-4">
              <img
                src={paymentData.qrCode}
                alt="UPI QR Code"
                className="w-64 h-64 mx-auto"
              />
              <p className="text-sm text-gray-600 mt-2">
                Scan QR code with any UPI app
              </p>
            </div>

            {/* UPI Link */}
            <a
              href={paymentData.upiLink}
              className="block bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition mb-3"
            >
              Pay with UPI App
            </a>

            {/* Verify Button */}
            <button
              onClick={handleVerifyPayment}
              disabled={verifying}
              className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition disabled:opacity-50"
            >
              {verifying ? "Verifying..." : "I've Completed Payment"}
            </button>

            <p className="text-xs text-gray-500 mt-3">
              Expires in {Math.floor(paymentData.expiresIn / 60)} minutes
            </p>
          </div>
        )}

        {!loading && paymentData && paymentMethod === "Online" && (
          <div className="text-center">
            <p className="text-lg font-semibold mb-2">
              Amount: ${paymentData.amount}
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Payment ID: {paymentData.paymentId}
            </p>

            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <p className="text-2xl mb-2">💳</p>
              <p className="text-sm text-gray-700">
                You will be redirected to PayPal to complete your payment securely.
              </p>
            </div>

            <button
              onClick={handlePayPalRedirect}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition mb-3"
            >
              Continue to PayPal
            </button>

            {verifying && (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
                <p className="text-sm">Verifying payment...</p>
              </div>
            )}

            <p className="text-xs text-gray-500 mt-3">
              Secure payment powered by PayPal
            </p>
          </div>
        )}

        {error && (
          <button
            onClick={initiatePayment}
            className="w-full mt-4 border border-purple-600 text-purple-600 py-2 rounded-lg hover:bg-purple-50 transition"
          >
            Try Again
          </button>
        )}
      </div>
    </div>
  );
}